import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
} from "firebase/auth";
import { auth, googleProvider } from "../firebase";

export class GoogleSignInError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "GoogleSignInError";
    this.code = code;
    this.cause = cause;
  }
}

const isDev = process.env.NODE_ENV !== "production";

function isUserCancelled(error) {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  return (
    code.includes("cancel") ||
    code.includes("popup-closed") ||
    message.includes("cancel") ||
    message.includes("12501") ||
    message.includes("user canceled") ||
    message.includes("user cancelled")
  );
}

function isNetworkError(error) {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").toLowerCase();
  return (
    code.includes("network") ||
    message.includes("network") ||
    code === "auth/network-request-failed"
  );
}

function safeStringifyError(error) {
  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== "{}") {
      return serialized;
    }
  } catch (_) {
    // ignore circular refs
  }

  try {
    return JSON.stringify({
      name: error?.name,
      code: error?.code,
      message: error?.message,
      nativeMessage: error?.nativeMessage ?? error?.cause?.message,
      stack: error?.stack,
    });
  } catch (_) {
    return String(error);
  }
}

function buildGoogleSignInLogPayload(error) {
  const nativeMessage =
    error?.nativeMessage ??
    error?.cause?.nativeMessage ??
    error?.cause?.message ??
    null;

  return {
    code: error?.code ?? error?.cause?.code ?? null,
    message: error?.message ?? null,
    nativeMessage,
    serialized: safeStringifyError(error),
    name: error?.name ?? null,
    stack: error?.stack ?? null,
  };
}

/** Diagnostic log for Google sign-in failures only. */
export function logGoogleSignInError(error) {
  const payload = buildGoogleSignInLogPayload(error);
  console.error("[GOOGLE_SIGNIN_ERROR]", payload);
}

/**
 * Full diagnostic log immediately before the generic provider-failed alert.
 * Does not change user-facing messages.
 */
export function logGoogleSignInErrorFull(error, context = {}) {
  const payload = {
    ...buildGoogleSignInLogPayload(error),
    ...context,
  };
  console.error("[GOOGLE_SIGNIN_ERROR_FULL]", payload);
}

function isGenericSocialLoginFailedMessage(error) {
  const code = error?.code || "";
  if (code === "auth/cancelled" || code === "auth/popup-closed-by-user") {
    return false;
  }
  if (code === "auth/missing-id-token") return false;
  if (code === "auth/network-request-failed") return false;
  if (isDev && code) return false;
  return true;
}

/** Safe summary of plugin result — no token values logged. */
function sanitizeSignInResultForLog(result) {
  const cred = result?.credential;
  return {
    hasUser: Boolean(result?.user),
    userUid: result?.user?.uid ? "(present)" : null,
    hasCredential: Boolean(cred),
    credentialProviderId: cred?.providerId ?? null,
    hasIdToken: Boolean(cred?.idToken),
    hasAccessToken: Boolean(cred?.accessToken),
    hasServerAuthCode: Boolean(cred?.serverAuthCode),
    idTokenLength: cred?.idToken ? String(cred.idToken).length : 0,
  };
}

function extractGoogleTokens(result) {
  const cred = result?.credential;
  const idToken = cred?.idToken ?? null;
  const accessToken = cred?.accessToken ?? null;
  return { idToken, accessToken };
}

function toGoogleSignInError(error, fallbackCode = "auth/google-sign-in-failed") {
  if (error instanceof GoogleSignInError) {
    return error;
  }
  if (isUserCancelled(error)) {
    return new GoogleSignInError(
      "auth/cancelled",
      "Google sign-in was cancelled.",
      error
    );
  }
  if (isNetworkError(error)) {
    return new GoogleSignInError(
      "auth/network-request-failed",
      "Network error during Google sign-in.",
      error
    );
  }
  const code =
    error?.code ||
    error?.cause?.code ||
    fallbackCode;
  return new GoogleSignInError(
    code,
    error?.message || "Google sign-in failed.",
    error
  );
}

async function signInWithGoogleNative() {
  let result;
  try {
    result = await FirebaseAuthentication.signInWithGoogle();
  } catch (error) {
    logGoogleSignInError(error);
    throw toGoogleSignInError(error);
  }

  const { idToken, accessToken } = extractGoogleTokens(result);

  if (!idToken) {
    const missingTokenError = new GoogleSignInError(
      "auth/missing-id-token",
      "Google sign-in did not return an id token.",
      result
    );
    logGoogleSignInError(missingTokenError);
    console.warn(
      "Google sign-in result missing idToken — summary:",
      sanitizeSignInResultForLog(result)
    );
    throw missingTokenError;
  }

  try {
    const credential = accessToken
      ? GoogleAuthProvider.credential(idToken, accessToken)
      : GoogleAuthProvider.credential(idToken);
    return await signInWithCredential(auth, credential);
  } catch (error) {
    logGoogleSignInError(error);
    throw toGoogleSignInError(error);
  }
}

async function signInWithGoogleWeb() {
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error) {
    if (isUserCancelled(error)) {
      throw new GoogleSignInError(
        "auth/cancelled",
        "Google sign-in was cancelled.",
        error
      );
    }
    if (isNetworkError(error)) {
      const wrapped = new GoogleSignInError(
        "auth/network-request-failed",
        "Network error during Google sign-in.",
        error
      );
      logGoogleSignInError(wrapped);
      throw wrapped;
    }
    logGoogleSignInError(error);
    throw toGoogleSignInError(error);
  }
}

export async function signInWithGoogle() {
  if (Capacitor.isNativePlatform()) {
    return signInWithGoogleNative();
  }
  return signInWithGoogleWeb();
}

export function getGoogleSignInErrorMessage(error, t) {
  const code = error?.code || "";

  if (code === "auth/cancelled" || code === "auth/popup-closed-by-user") {
    return t("auth.popup_closed", {
      defaultValue: "The sign-in window was closed before completion.",
    });
  }

  if (code === "auth/missing-id-token") {
    return t("auth.google_token_missing", {
      defaultValue: "Google sign-in could not be completed. Please try again.",
    });
  }

  if (code === "auth/network-request-failed") {
    return t("common.offline_error", {
      defaultValue: "No internet connection.",
    });
  }

  if (isDev && code) {
    return `Google sign-in failed: ${code}`;
  }

  if (isGenericSocialLoginFailedMessage(error)) {
    logGoogleSignInErrorFull(error, {
      userMessageKey: "auth.social_login_failed",
    });
  }

  return t("auth.social_login_failed", {
    defaultValue: "Could not sign in with this provider.",
  });
}
